import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Send } from "lucide-react";

export default function InviteCandidate() {
  const [form, setForm] = useState({ invitee_name: "", invitee_email: "", municipality_name: "", office: "", sender_name: "", message: "" });
  const [hp, setHp] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const valid = form.invitee_name.length >= 2 && /.+@.+\..+/.test(form.invitee_email) && form.sender_name.length >= 2;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, website: hp };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      payload.website = hp || "";
      const r = await api.post("/public/invitations", payload);
      setDone(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done)
    return (
      <div className="wyv-container py-16">
        <Card className="max-w-xl mx-auto"><CardContent className="p-8 text-center">
          <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="h-6 w-6 text-emerald-700" />
          </div>
          <h1 className="font-heading font-bold text-xl" data-testid="invite-success-title">Invitation recorded</h1>
          <p className="text-sm text-muted-foreground mt-3">{done.message}</p>
        </CardContent></Card>
      </div>
    );

  return (
    <div className="wyv-container py-12 sm:py-16">
      <div className="max-w-2xl">
        <h1 className="font-heading font-bold text-3xl sm:text-4xl text-foreground" data-testid="invite-title">Invite a candidate</h1>
        <p className="text-muted-foreground mt-3 leading-relaxed">
          Know a candidate who should offer Witness Your Vote to their supporters? Tell us who they are — our team
          prepares a neutral invitation for review before anything is sent. Invitations are open to every legitimate
          registered candidate equally.
        </p>
      </div>
      <form onSubmit={submit} className="mt-8 max-w-xl">
        <Card><CardContent className="p-6 sm:p-8 space-y-5">
          <input type="text" value={hp} onChange={(e) => setHp(e.target.value)} className="hp-field" tabIndex={-1} autoComplete="off" aria-hidden="true" name="website" />
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Candidate name *</Label>
              <Input className="mt-1.5" value={form.invitee_name} onChange={(e) => set("invitee_name", e.target.value)} data-testid="invite-candidate-name" />
            </div>
            <div>
              <Label>Candidate email *</Label>
              <Input type="email" className="mt-1.5" value={form.invitee_email} onChange={(e) => set("invitee_email", e.target.value)} data-testid="invite-candidate-email" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Municipality</Label>
              <Input className="mt-1.5" value={form.municipality_name} onChange={(e) => set("municipality_name", e.target.value)} placeholder="e.g. City of Kawartha Lakes" />
            </div>
            <div>
              <Label>Office / Ward</Label>
              <Input className="mt-1.5" value={form.office} onChange={(e) => set("office", e.target.value)} placeholder="e.g. Councillor — Ward 3" />
            </div>
          </div>
          <div>
            <Label>Your name *</Label>
            <Input className="mt-1.5" value={form.sender_name} onChange={(e) => set("sender_name", e.target.value)} data-testid="invite-sender-name" />
          </div>
          <div>
            <Label>Personal message (optional)</Label>
            <Textarea className="mt-1.5" rows={4} value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="invite-message" />
          </div>
          <Button type="submit" className="w-full h-11" disabled={!valid || submitting} data-testid="invite-submit-button">
            <Send className="h-4 w-4 mr-2" /> {submitting ? "Submitting…" : "Submit invitation"}
          </Button>
        </CardContent></Card>
      </form>
    </div>
  );
}
